// Design tokens do Dashboard de Métricas.
// Referência: projeto Claude Design "Dashboard Metricas" + UI-UX.md.
//
// Regras da paleta:
// - Neutros para estrutura, roxo SÓ como acento (aba ativa, série principal, links).
// - Cor semântica comunica estado; nunca decora. Máx. 1 estado por card.

export const T = {
    // superfícies / neutros
    page: '#f4f4f6',
    surface: '#ffffff',
    border: '#e6e6ec',
    borderInput: '#e2e2ea',
    divider: '#eeeef2',
    dividerSoft: '#f3f3f6',
    track: '#f2f2f5',
    subtle: '#fafafb',

    // texto
    text: '#17172b',
    text2: '#4b4b5c',
    label: '#6b6b7b',
    muted: '#8b8b9a',
    empty: '#a0a0af',

    // marca (acento)
    brand: '#6d28d9',
    brand2: '#7c3aed',
    brand3: '#a78bfa',
    brand4: '#c4b5fd',
    brandSoft: '#f1ecfd',
    brandGhost: '#ddd6fe',

    // semântica
    ok: '#15803d',
    okBg: '#dcfce7',
    okFill: '#16a34a',
    warn: '#b45309',
    warnBg: '#fdf3e4',
    warnBorder: '#f0e0c8',
    warnFill: '#d97706',
    crit: '#b91c1c',
    critBg: '#fdeaea',
    critBorder: '#f2d3d3',
    critFill: '#dc2626',
};

// Escala divergente de sentimento (exceção autorizada ao "cor só semântica").
export const SENTIMENT = {
    5: { color: '#15803d', label: 'Muito positivo' },
    4: { color: '#4ade80', label: 'Positivo' },
    3: { color: '#e8e8ee', label: 'Neutro' },
    2: { color: '#f59e0b', label: 'Negativo' },
    1: { color: '#dc2626', label: 'Muito negativo' },
};

// Ordem de leitura da barra de composição: positivo -> negativo.
export const SENTIMENT_ORDER = [5, 4, 3, 2, 1];

// Tons semânticos usados pelos primitivos (KpiCard, Meter, Badge...).
export const TONES = {
    neutral: { value: T.text, border: T.border, badgeBg: T.track, badgeText: T.label, fill: T.brand, shadow: 'none' },
    ok: { value: T.ok, border: T.border, badgeBg: T.okBg, badgeText: T.ok, fill: T.okFill, shadow: 'none' },
    warn: { value: T.warn, border: T.warnBorder, badgeBg: T.warnBg, badgeText: T.warn, fill: T.warnFill, shadow: 'none' },
    crit: { value: T.crit, border: T.critBorder, badgeBg: T.critBg, badgeText: T.crit, fill: T.critFill, shadow: '0 1px 2px rgba(190,24,24,0.06)' },
};

// Rampas de 4 tons para sparkline (do mais claro ao cheio).
export const SPARK_RAMP = {
    neutral: ['#e9e6f8', '#d9d2f4', '#c6bcef', T.brand],
    ok: ['#dcf3e3', '#c3ead0', '#a3dcb6', T.okFill],
    warn: ['#f8ecd9', '#f4e0bf', '#eecfa0', T.warnFill],
    crit: ['#fbe3e3', '#f6cccc', '#efadad', T.critFill],
};

export const PAINEL_DESIGN = {
    // shell
    root: 'flex h-full w-full flex-col overflow-hidden',
    // aplicado inline no root: Tailwind não é a melhor ferramenta para um stack
    // de família com fallbacks, e a fonte é escopada só a esta página.
    fontStack: "Manrope, Inter, ui-sans-serif, system-ui, sans-serif",
    topbar: 'flex items-center justify-between gap-6 px-4 md:px-8 min-h-[60px] bg-white border-b border-[#e6e6ec] flex-shrink-0',
    tabsList: 'flex items-center gap-0.5 min-w-0 overflow-x-auto scrollbar-hide bg-transparent p-0 h-auto',
    tab: 'flex-shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-[13.5px] font-semibold text-[#6b6b7b] transition-colors hover:bg-[#f6f6f8] hover:text-[#17172b] data-[state=active]:bg-[#f1ecfd] data-[state=active]:text-[#6d28d9] data-[state=active]:shadow-none',
    filters: 'flex items-center gap-2.5 flex-shrink-0',
    filterChip:
        'flex items-center gap-2 min-h-[34px] px-3 border border-[#e2e2ea] rounded-lg bg-white text-[13px] font-semibold text-[#6b6b7b] whitespace-nowrap',
    scroll: 'flex-1 overflow-y-auto bg-[#f4f4f6]',
    container: 'max-w-[1360px] mx-auto px-4 md:px-8 pt-7 pb-14',

    // cabeçalho de aba
    pageHead: 'flex items-end justify-between gap-4 mb-3.5 flex-wrap',
    pageTitle: 'text-[20px] font-extrabold tracking-[-0.02em] text-[#17172b]',
    pageSubtitle: 'mt-[3px] text-[13px] font-medium text-[#6b6b7b]',

    // cards
    card: 'bg-white border rounded-[14px]',
    kpiCard: 'bg-white border rounded-[14px] px-[22px] pt-5 pb-[18px]',
    blockCard: 'bg-white border border-[#e6e6ec] rounded-[14px] px-6 py-[22px]',
    strip: 'grid bg-white border border-[#e6e6ec] rounded-[14px] overflow-hidden',
    stripCell: 'px-[22px] py-4',

    // rótulos
    kpiLabel: 'text-[12.5px] font-bold text-[#6b6b7b]',
    stripLabel: 'text-[12px] font-bold text-[#8b8b9a]',
    blockTitle: 'text-[16px] font-extrabold tracking-[-0.015em] text-[#17172b]',
    blockSubtitle: 'mt-[3px] text-[12.5px] font-medium text-[#6b6b7b]',
    cardTitle: 'text-[15px] font-extrabold tracking-[-0.015em] text-[#17172b]',
    note: 'text-[11.5px] font-semibold text-[#8b8b9a]',

    // números
    kpiValue: 'text-[46px] leading-none font-extrabold tracking-[-0.03em] tabular-nums',
    kpiValueSm: 'text-[40px] leading-none font-extrabold tracking-[-0.03em] tabular-nums',
    stripValue: 'text-[26px] font-extrabold tracking-[-0.02em] tabular-nums',

    // tabela
    tableHead:
        'px-[22px] py-3.5 bg-[#fafafb] border-b border-[#eeeef2] text-[11px] font-extrabold uppercase tracking-[0.04em] text-[#8b8b9a]',
    tableRow: 'px-[22px] py-[15px] border-b border-[#f3f3f6] transition-colors hover:bg-[#fafafb]',
    tableFoot: 'flex items-center justify-between px-[22px] py-3 bg-[#fafafb]',

    // rodapés / ações
    insight: 'mt-5 pt-4 border-t border-[#eeeef2] text-[12.5px] font-medium text-[#6b6b7b]',
    link: 'text-[12px] font-bold text-[#6d28d9] hover:underline cursor-pointer',
    ghostButton:
        'flex items-center gap-2 min-h-[32px] px-3 border border-[#e2e2ea] rounded-lg bg-white text-[12.5px] font-semibold text-[#6b6b7b] whitespace-nowrap transition-colors hover:border-[#c9c9d6] hover:text-[#17172b] disabled:opacity-50 disabled:cursor-not-allowed',

    // grids
    grid3: 'grid grid-cols-1 md:grid-cols-3 gap-4',
    grid4: 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4',
    grid2: 'grid grid-cols-1 lg:grid-cols-2 gap-4',
    animate: 'mt-0 animate-in fade-in slide-in-from-bottom-2 duration-300',
};

export default PAINEL_DESIGN;
